import { inject } from 'vue';
import axios from 'axios';
import { isTokenValid, rememberLoginDestination } from '@/helpers/auth';

const redirectToLogin = () => {
  rememberLoginDestination(window.location.pathname + window.location.search);
  localStorage.setItem('aiq_token', '');
  window.location.href = '/auth/login';
};

const FILENAME_PATTERN = /filename="?([^";]+)"?/;

// Canonical blob-download primitive: new code uses this, never inline
// createObjectURL + anchor click.
// TODO: migrate the ~16 pages still inlining the pattern here.
export const saveBlob = (blob, filename) => {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
};

export function useApi() {
  const loader = inject('loader');
  const defaultOptions = {
    showLoader: true,
    responseType: 'json'
  };

  const makeRequest = async (
    method,
    url,
    data = null,
    params = {},
    options = {}
  ) => {
    options = {...defaultOptions, ...options};

    if (loader && options.showLoader) {
      loader.increment();
    }

    const endpoint = url;
    url = window.$udabApiUrl + url;
    if (!endpoint.startsWith('/saml/')) {
      const token = localStorage.getItem('aiq_token');
      if (!token || !(await isTokenValid(token))) {
        redirectToLogin();
        return;
      }
    }

    const aiqToken = localStorage.getItem('aiq_token');
    try {
      // Extract non-axios options
      const { showLoader, ...axiosOptions } = options;

      const response = await axios({
        method,
        url,
        data,
        params,
        ...axiosOptions, // Spread axios options (signal, responseType, etc.)
        headers: {
          'X-AIQ-TOKEN': `Bearer ${aiqToken}`,
          'X-AIQ-CLIENT-TYPE': 'AIQ-client',
          ...axiosOptions.headers // Preserve any custom headers
        }
      });

      if (response?.data?.unauthorized) {
        redirectToLogin();
        return;
      }

      return response.data;
    } finally {
      if (loader && options.showLoader) {
        loader.decrement();
      }
    }
  };

  const get = (url, params, options = {}) =>
    makeRequest('get', url, null, params, options);

  const post = (url, data, options = {}) =>
    makeRequest('post', url, data, {}, options);

  const put = (url, data, options = {}) =>
    makeRequest('put', url, data, {}, options);

  const patch = (url, data, options = {}) =>
    makeRequest('patch', url, data, {}, options);

  const deleteRequest = (url, data, params, options = {}) =>
    makeRequest('delete', url, data, params, options);

  const del = (url, data, params, options = {}) => deleteRequest(url, data, params, options);

  // Authed file download. A plain link can't carry the JWT header, so the file
  // is fetched as a blob and handed to the browser; the server's
  // Content-Disposition names it when present. Resolves true once the file is
  // handed over, false on 204 (nothing to download — no empty file is saved).
  const download = async (url, params = {}, fallbackFilename = 'download.csv') => {
    const token = localStorage.getItem('aiq_token');
    if (!token || !(await isTokenValid(token))) {
      redirectToLogin();
      return;
    }
    if (loader) {
      loader.increment();
    }
    try {
      const response = await axios({
        method: 'get',
        url: window.$udabApiUrl + url,
        params,
        responseType: 'blob',
        headers: {
          'X-AIQ-TOKEN': `Bearer ${token}`,
          'X-AIQ-CLIENT-TYPE': 'AIQ-client'
        }
      });
      if (response.status === 204) return false;
      const disposition = response.headers?.['content-disposition'] || '';
      const match = FILENAME_PATTERN.exec(disposition);
      saveBlob(response.data, match ? match[1] : fallbackFilename);
      return true;
    } finally {
      if (loader) {
        loader.decrement();
      }
    }
  };

  const postFormData = async (url, data, params = {}, options = {}) => {
    options = {...defaultOptions, ...options};

    if (options.showLoader) {
      loader.increment();
    }

    url = window.$udabApiUrl + url;
    if (!url.endsWith('/saml/token') && !url.endsWith('/saml/refresh')) {
      const token = localStorage.getItem('aiq_token');
      if (!token || !(await isTokenValid(token))) {
        redirectToLogin();
        return;
      }
    }

    const aiqToken = localStorage.getItem('aiq_token');
    try {
      const response = await axios.post(url, data, {
        params,
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-AIQ-TOKEN': `Bearer ${aiqToken}`,
          'X-AIQ-CLIENT-TYPE': 'AIQ-client'
        },
        responseType: options.responseType
      });
      return response.data;
    } finally {
      if (options.showLoader) {
        loader.decrement();
      }
    }
  };

  const getStrategyActivities = async (strategyId, channel = null) => {
    console.log(`[API] Calling getStrategyActivities for strategy ${strategyId}, channel: ${channel}`);
    let url = `/strategy/${strategyId}/activities`;
    if (channel) {
      url += `?channel=${channel}`;
    }
    console.log(`[API] Full URL: ${url}`);

    try {
      const response = await get(url, {}, {showLoader: false});
      console.log('[API] getStrategyActivities response:', response);
      return response;
    } catch (error) {
      console.error('[API] getStrategyActivities error:', error);
      throw error;
    }
  };

  const getActivityContacts = async (activityId, offset = 0, limit = 100) => {
    console.log(`[API] Fetching contacts for activity ${activityId} (offset: ${offset}, limit: ${limit})`);
    try {
      const aiqToken = localStorage.getItem('aiq_token');
      const response = await axios({
        method: 'get',
        url: window.$udabApiUrl + `/strategy-activity/${activityId}/contacts`,
        params: {offset, limit},
        headers: {
          'X-AIQ-TOKEN': `Bearer ${aiqToken}`,
          'X-AIQ-CLIENT-TYPE': 'AIQ-client'
        }
      });
      console.log(`[API] getActivityContacts response: Got ${response.data?.length || 0} contacts`);
      if (response.data && response.data.length > 0) {
        console.log('[API] Sample contact data:', {
          project: {
            id: response.data[0].project_id,
            name: response.data[0].project_name
          },
          company: {
            id: response.data[0].company_id,
            name: response.data[0].company_name
          },
          industry: response.data[0].primary_industry
        });
      }
      return response.data;
    } catch (error) {
      console.error('[API] getActivityContacts error:', error);
      throw error;
    }
  };

  const exportActivityContacts = async (activityId) => {
    console.log(`[API] Exporting contacts for activity ${activityId}`);
    try {
      const aiqToken = localStorage.getItem('aiq_token');
      const response = await axios({
        method: 'get',
        url: window.$udabApiUrl + `/strategy-activity/${activityId}/contacts/export`,
        responseType: 'blob',
        headers: {
          'X-AIQ-TOKEN': `Bearer ${aiqToken}`,
          'X-AIQ-CLIENT-TYPE': 'AIQ-client'
        }
      });
      console.log(`[API] Export response status: ${response.status}, blob size: ${response.data.size}`);
      return response.data;
    } catch (error) {
      console.error('[API] exportActivityContacts error:', error);
      throw error;
    }
  };

  const getActivityProspects = async (activityId, offset = 0, limit = 100) => {
    try {
      const aiqToken = localStorage.getItem('aiq_token');
      const response = await axios({
        method: 'get',
        url: window.$udabApiUrl + `/strategy-activity/${activityId}/prospects`,
        params: {offset, limit},
        headers: {
          'X-AIQ-TOKEN': `Bearer ${aiqToken}`,
          'X-AIQ-CLIENT-TYPE': 'AIQ-client'
        }
      });
      return response.data;
    } catch (error) {
      console.error('[API] getActivityProspects error:', error);
      throw error;
    }
  };

  const exportActivityProspects = async (activityId) => {
    console.log(`[API] Exporting prospects for activity ${activityId}`);
    try {
      const aiqToken = localStorage.getItem('aiq_token');
      const response = await axios({
        method: 'get',
        url: window.$udabApiUrl + `/strategy-activity/${activityId}/prospects/export`,
        responseType: 'blob',
        headers: {
          'X-AIQ-TOKEN': `Bearer ${aiqToken}`,
          'X-AIQ-CLIENT-TYPE': 'AIQ-client'
        }
      });
      console.log(`[API] Export response status: ${response.status}, blob size: ${response.data.size}`);
      return response.data;
    } catch (error) {
      console.error('[API] exportActivityProspects error:', error);
      throw error;
    }
  };

  const getActivityLogs = async (activityId) => {
    console.log(`[API] Fetching logs for activity ${activityId}`);
    try {
      const aiqToken = localStorage.getItem('aiq_token');
      const response = await axios({
        method: 'get',
        url: window.$udabApiUrl + `/strategy-activity/${activityId}/logs`,
        responseType: 'text',
        headers: {
          'X-AIQ-TOKEN': `Bearer ${aiqToken}`,
          'X-AIQ-CLIENT-TYPE': 'AIQ-client'
        }
      });
      return response.data;
    } catch (error) {
      console.error('[API] getActivityLogs error:', error);
      throw error;
    }
  };

  const getActivityReport = async (reportDate) => {
    const response = await get('/strategy-activity/report', { report_date: reportDate }, { showLoader: false });
    return response;
  };

  const getActivityTrends = async (reportDate, days = 7) => {
    const response = await get('/strategy-activity/report/trends', { report_date: reportDate, days }, { showLoader: false });
    return response;
  };

  const getSequencerRecordTypes = async () => {
    const response = await get('/strategy-activity/record-types', {}, { showLoader: false });
    return response;
  };

  const getRemovalLogs = async (removalId) => {
    console.log(`[API] Fetching logs for removal ${removalId}`);
    try {
      const aiqToken = localStorage.getItem('aiq_token');
      const response = await axios({
        method: 'get',
        url: window.$udabApiUrl + `/strategy-removal/${removalId}/logs`,
        responseType: 'text',
        headers: {
          'X-AIQ-TOKEN': `Bearer ${aiqToken}`,
          'X-AIQ-CLIENT-TYPE': 'AIQ-client'
        }
      });
      return response.data;
    } catch (error) {
      console.error('[API] getRemovalLogs error:', error);
      throw error;
    }
  };

  const pollNewActivities = async (strategyId, minId, mode = 'interactive') => {
    return get(`/strategies/${strategyId}/poll-activities?min_id=${minId}&mode=${mode}`, {}, {showLoader: false});
  };

  const createStrategyRemoval = async (strategyId, removalData) => {
    console.log(`[API] Creating removal for strategy ${strategyId}`, removalData);
    try {
      const response = await post(`/strategies/${strategyId}/removals`, removalData);
      console.log('[API] createStrategyRemoval response:', response);
      return response;
    } catch (error) {
      console.error('[API] createStrategyRemoval error:', error);
      throw error;
    }
  };

  const getStrategyRemovals = async (strategyId) => {
    console.log(`[API] Fetching removals for strategy ${strategyId}`);
    try {
      const response = await get(`/strategies/${strategyId}/removals`, {}, {showLoader: false});
      console.log('[API] getStrategyRemovals response:', response);
      return response;
    } catch (error) {
      console.error('[API] getStrategyRemovals error:', error);
      throw error;
    }
  };

  const getRemovalDetails = async (removalId) => {
    console.log(`[API] Fetching details for removal ${removalId}`);
    try {
      const response = await get(`/strategy-removal/${removalId}`, {}, {showLoader: false});
      console.log('[API] getRemovalDetails response:', response);
      return response;
    } catch (error) {
      console.error('[API] getRemovalDetails error:', error);
      throw error;
    }
  };

  const getRemovalProspects = async (removalId, params = {}) => {
    console.log(`[API] Fetching prospects for removal ${removalId}`, params);
    try {
      const response = await get(`/strategy-removal/${removalId}/prospects`, params, {showLoader: false});
      console.log(`[API] getRemovalProspects response: Got ${response?.prospects?.length || 0} prospects`);
      return response;
    } catch (error) {
      console.error('[API] getRemovalProspects error:', error);
      throw error;
    }
  };

  const updateRemovalProspect = async (removalId, prospectId, updateData) => {
    console.log(`[API] Updating prospect ${prospectId} for removal ${removalId}`, updateData);
    try {
      const response = await patch(`/strategy-removal/${removalId}/prospects/${prospectId}`, updateData);
      console.log('[API] updateRemovalProspect response:', response);
      return response;
    } catch (error) {
      console.error('[API] updateRemovalProspect error:', error);
      throw error;
    }
  };

  const processRemoval = async (removalId) => {
    console.log(`[API] Processing removal ${removalId}`);
    try {
      const response = await post(`/strategy-removal/${removalId}/process`);
      console.log('[API] processRemoval response:', response);
      return response;
    } catch (error) {
      console.error('[API] processRemoval error:', error);
      throw error;
    }
  };

  const getRemovalStatus = async (removalId) => {
    console.log(`[API] Fetching status for removal ${removalId}`);
    try {
      const response = await get(`/strategy-removal/${removalId}/status`, {}, {showLoader: false});
      console.log('[API] getRemovalStatus response:', response);
      return response;
    } catch (error) {
      console.error('[API] getRemovalStatus error:', error);
      throw error;
    }
  };

  const pollRemovalStatus = async (removalId) => {
    return getRemovalStatus(removalId);
  };

  const getAvailableRemovalCriteria = async (strategyId) => {
    try {
      console.log(`[API] Fetching available removal criteria for strategy ${strategyId}`);
      const response = await get(`/strategies/${strategyId}/available-removal-criteria`);
      console.log('[API] getAvailableRemovalCriteria response:', response);
      return response;
    } catch (error) {
      console.error('[API] getAvailableRemovalCriteria error:', error);
      throw error;
    }
  };

  const getAppointmentEmails = (params) => get('/appointment-emails/', params);
  const getAppointmentEmailAccounts = () => get('/appointment-emails/accounts', {}, { showLoader: false });
  const getAppointmentEmailIndustries = () => get('/appointment-emails/industries', {}, { showLoader: false });
  const getAppointmentEmailDevOutcomes = () => get('/appointment-emails/dev-outcomes', {}, { showLoader: false });
  const getAppointmentEmail = (id, options = {}) => get(`/appointment-emails/${id}`, {}, options);
  const getAppointmentEmailTranscript = (id, options = {}) => get(`/appointment-emails/${id}/transcript`, {}, options);
  const transcribeAppointmentEmail = (id) => post(`/appointment-emails/${id}/transcribe`, {}, { showLoader: false });
  const generateAppointmentHighlights = (id) => post(`/appointment-emails/${id}/generate-highlights`, {}, { showLoader: false });
  const transcribeAppointmentDictation = (formData) => postFormData('/appointment-emails/dictation', formData, {}, { showLoader: false });
  const getAppointmentEmailPreview = (id, options = {}) => get(`/appointment-emails/${id}/preview`, {}, options);
  const updateAppointmentEmail = (id, data) => patch(`/appointment-emails/${id}`, data);
  const addAppointmentHighlight = (id, data, options = {}) => post(`/appointment-emails/${id}/highlights`, data, options);
  const updateAppointmentHighlight = (id, highlightId, data, options = {}) => patch(`/appointment-emails/${id}/highlights/${highlightId}`, data, options);
  const deleteAppointmentHighlight = (id, highlightId, options = {}) => del(`/appointment-emails/${id}/highlights/${highlightId}`, null, {}, options);
  const reorderAppointmentHighlights = (id, ids, options = {}) => put(`/appointment-emails/${id}/highlights/order`, { ids }, options);
  const editAppointmentEmailField = (id, field, value) =>
    put(`/appointment-emails/${id}/fields/${field}`, { value }, { showLoader: false });
  const revertAppointmentEmailField = (id, field) =>
    del(`/appointment-emails/${id}/fields/${field}`, null, {}, { showLoader: false });
  const editAppointmentEmailTranscript = (id, text) =>
    put(`/appointment-emails/${id}/transcript`, { text }, { showLoader: false });
  const getAppointmentEmailCalls = (id) => get(`/appointment-emails/${id}/calls`, {}, { showLoader: false });
  const sendAppointmentEmailTest = (id, recipients) =>
    post(`/appointment-emails/${id}/send-test`, { recipients }, { showLoader: false });
  const changeAppointmentEmailCall = (id, sfTaskId) =>
    put(`/appointment-emails/${id}/call`, { sf_task_sf_id: sfTaskId }, { showLoader: false });
  const submitAppointmentFeedback = (id, data) => post(`/appointment-emails/${id}/feedback`, data);
  const sendAppointmentEmail = (id, recipients, options = {}) =>
    post(`/appointment-emails/${id}/send`, { recipients }, options);
  const resendAppointmentEmail = (id, recipients, options = {}) =>
    post(`/appointment-emails/${id}/resend`, recipients ? { recipients } : {}, options);
  const resendAppointmentEmailRecipient = (id, recipient, options = {}) =>
    post(`/appointment-emails/${id}/recipients/resend`, { recipient }, options);
  const cancelAppointmentEmail = (id, options = {}) => post(`/appointment-emails/${id}/cancel`, {}, options);
  const regenerateAppointmentEmail = (id, options = {}) => post(`/appointment-emails/${id}/regenerate`, {}, options);

  const getAppointmentCalls = (params) => get('/appointment-calls', params);
  const getAppointmentCallFilters = () => get('/appointment-calls/filters', {}, { showLoader: false });
  const getAppointmentCall = (id, options = {}) => get(`/appointment-calls/${id}`, {}, options);
  const getAppointmentCallTranscript = (id, options = {}) => get(`/appointment-calls/${id}/transcript`, {}, options);
  const getAppointmentCallBuildingImage = (id) =>
    get(`/appointment-calls/${id}/building-image`, {}, { responseType: 'blob', showLoader: false });
  const downloadAppointmentCallTranscript = (id) =>
    download(`/appointment-calls/${id}/transcript/download`, {}, `${id}.txt`);
  const exportAppointmentCallTranscripts = (ids) =>
    download('/appointment-calls/export', { ids: ids.join(',') }, 'appointment-call-transcripts.zip');
  // Background "download all": the POST returns { export_id, download_url, expires_at };
  // the GET returns the job's { state, total, exported, size_bytes, error }.
  const requestAppointmentCallExport = (params) => post('/appointment-calls/exports', params);
  const submitAppointmentCallSalesmanship = (id, data) => put(`/appointment-calls/${id}/salesmanship-scorecard`, data);
  const getAppointmentCallExport = (id) => get(`/appointment-calls/exports/${id}`, {}, { showLoader: false });
  const getViewPreferences = (page, options = {}) => get(`/view-preferences/${page}`, {}, { showLoader: false, ...options });
  const putViewPreferences = (page, preferences, options = {}) =>
    put(`/view-preferences/${page}`, { preferences }, { showLoader: false, ...options });

  const getApiTokens = () => get('/api-tokens');
  const createApiToken = (data) => post('/api-tokens', data);
  const updateApiToken = (id, data) => put('/api-tokens/' + id, data);
  const deleteApiToken = (id) => del('/api-tokens/' + id);

  return {
    get,
    post,
    put,
    patch,
    del,
    deleteRequest,
    download,
    postFormData,
    getAppointmentEmails,
    getAppointmentEmailAccounts,
    getAppointmentEmailIndustries,
    getAppointmentEmailDevOutcomes,
    getAppointmentEmail,
    getAppointmentEmailTranscript,
    transcribeAppointmentEmail,
    generateAppointmentHighlights,
    transcribeAppointmentDictation,
    getAppointmentEmailPreview,
    updateAppointmentEmail,
    addAppointmentHighlight,
    updateAppointmentHighlight,
    deleteAppointmentHighlight,
    reorderAppointmentHighlights,
    editAppointmentEmailField,
    revertAppointmentEmailField,
    editAppointmentEmailTranscript,
    getAppointmentEmailCalls,
    changeAppointmentEmailCall,
    sendAppointmentEmailTest,
    submitAppointmentFeedback,
    sendAppointmentEmail,
    resendAppointmentEmail,
    resendAppointmentEmailRecipient,
    cancelAppointmentEmail,
    regenerateAppointmentEmail,
    getAppointmentCalls,
    getAppointmentCallFilters,
    getAppointmentCall,
    getAppointmentCallTranscript,
    getAppointmentCallBuildingImage,
    downloadAppointmentCallTranscript,
    exportAppointmentCallTranscripts,
    requestAppointmentCallExport,
    submitAppointmentCallSalesmanship,
    getAppointmentCallExport,
    getViewPreferences,
    putViewPreferences,
    getApiTokens,
    createApiToken,
    updateApiToken,
    deleteApiToken,
    getStrategyActivities,
    getActivityContacts,
    exportActivityContacts,
    getActivityProspects,
    exportActivityProspects,
    getActivityLogs,
    getActivityReport,
    getActivityTrends,
    getSequencerRecordTypes,
    getRemovalLogs,
    pollNewActivities,
    createStrategyRemoval,
    getStrategyRemovals,
    getRemovalDetails,
    getRemovalProspects,
    updateRemovalProspect,
    processRemoval,
    getRemovalStatus,
    pollRemovalStatus,
    getAvailableRemovalCriteria
  };
}
